import { useReducedMotion } from 'framer-motion'
import { MASTERY_COLORS, MASTERY_MAX } from '../../types'
import { useLocale } from '../../i18n/context'

const MASTERY_ICONS: Record<number, string> = {
  1: '🔴',
  2: '🟠',
  3: '🟡',
  4: '💛',
  5: '🟢',
  6: '💚',
  7: '🔵',
  8: '🌟',
}

interface Props {
  level: number
  size?: 'sm' | 'md' | 'dot'
}

/**
 * Mastery indicator.
 *
 * Two render modes:
 *  - 'md' / 'sm': icon + small colored dot. No text — the level is a number,
 *    not a word (the scale states itself; nothing here spells it out).
 *  - 'dot': single-hue magnitude bar (Wave 3 §1/§5, ADR-014 amendment). Same
 *    visual model as MasteryMeter — 8 discrete segments, fill length carries
 *    the level, one calm accent hue. Sized for the Learn card's top-right corner.
 *    Fill-change motion via CSS background-color transition (respects reduced-motion):
 *    grows on level-up, shrinks on level-drop, still on no-change. No pulse, no
 *    green/red tint — in-the-moment answer feedback is owned by the input overlay.
 *
 * Both modes are visual-only (icon/dot/fill carry the level); `aria-label` on
 * the 'dot' mode's `role="img"` wrapper is the one place an accessible name
 * is stated in words, and it states the numeral ("Mastery: 4 of 8"), not a
 * per-level word — there is no per-level word to state.
 */
export default function MasteryBadge({ level, size = 'md' }: Props) {
  const { tr } = useLocale()
  const clampedLevel = Math.max(1, Math.min(MASTERY_MAX, Math.round(level)))
  const color = MASTERY_COLORS[clampedLevel]
  const icon = MASTERY_ICONS[clampedLevel]
  const reduceMotion = useReducedMotion()

  const dotSize = size === 'sm' ? 8 : 10
  const fontSize = size === 'sm' ? 12 : 13
  const gap = size === 'sm' ? 5 : 6

  if (size === 'dot') {
    // Single-hue fill ramp — same three-range mapping as MasteryMeter.fillFor().
    // Never red: low end is calm steel-grey, not an alarm color (Wave 3 §3).
    const fill =
      clampedLevel >= 6 ? 'var(--mastery-fill-high)'
      : clampedLevel >= 4 ? 'var(--mastery-fill-mid)'
      : 'var(--mastery-fill-low)'

    // Fixed-size pips (5×8 px, 3 px gap) — each segment is an independent
    // rectangle, no flex-proportional sizing, so the count is legible at a
    // glance on a mobile card. Cell count derived from MASTERY_MAX so a future
    // ladder change updates the indicator automatically (ADR-014 amendment 2026-07-07).
    return (
      <div
        role="img"
        aria-label={tr.mastery.ariaLabel(clampedLevel, MASTERY_MAX)}
        style={{ display: 'flex', gap: 3, flexShrink: 0 }}
      >
        {Array.from({ length: MASTERY_MAX }, (_, i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              width: 5,
              height: 8,
              flexShrink: 0,
              borderRadius: 2,
              backgroundColor: i < clampedLevel ? fill : 'var(--mastery-track)',
              transition: reduceMotion ? 'none' : 'background-color 0.25s ease-in-out',
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        fontFamily: 'var(--font-ui)',
        fontSize,
        fontWeight: 500,
        color,
        lineHeight: 1,
      }}
    >
      <span style={{ fontSize: size === 'sm' ? 10 : 12, lineHeight: 1 }}>{icon}</span>
      <span
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
    </span>
  )
}
